import { useKeyboardShortcut } from '@bestseller-bit/frontend-community.hooks.use-keyboard-shortcut';
import { Add } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { GridApi, GridReadyEvent, ICellRendererParams, RowDragEndEvent } from 'ag-grid-community';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import Container from 'src/components/atoms/Container';
import { DataTable } from 'src/components/organisms/DataTable/DataTable';
import { DeleteDialog } from 'src/components/organisms/DeleteDialog/DeleteDialog';
import { SmartModuleTitleCard } from 'src/components/organisms/SmartModuleTitleCard/SmartModuleTitleCard';
import { TableWrapper } from 'src/components/styled/TableWrapper';
import { useCustomAgGridNavigation } from 'src/hooks/ag-grid/useCustomAgGridNavigation';
import { buildInventoryMasterDataReorderInputs } from 'src/hooks/inventory-master-data/save-changes/buildInventoryMasterDataReorderInputs';
import { useInventoryMasterDataDeleteChanges } from 'src/hooks/inventory-master-data/save-changes/useInventoryMasterDataDeleteChanges';
import { useInventoryMasterDataUpdateChanges } from 'src/hooks/inventory-master-data/save-changes/useInventoryMasterDataUpdateChanges';
import {
    InventoryMasterDataRow,
    useInventoryMasterDataRows,
} from 'src/hooks/inventory-master-data/table/useInventoryMasterDataRows';
import { useSnackbar } from 'src/hooks/snackbar/useSnackbar';
import { useDialog } from 'src/hooks/useDialog';
import { AddFurnitureDialog } from './AddFurnitureDialog';
import { defaultColDef, defaultColumns } from './data/columns';

export const InventoryMasterData: FC = () => {
    const { isOpen, openDialog, closeDialog } = useDialog();
    const { isOpen: isDeleteOpen, openDialog: openDeleteDialog, closeDialog: closeDeleteDialog } = useDialog();
    const { data: rows, loading } = useInventoryMasterDataRows();
    const [selectedRow, setSelectedRow] = useState<InventoryMasterDataRow | null>(null);
    const [furnitureToDelete, setFurnitureToDelete] = useState<InventoryMasterDataRow | null>(null);
    const [deleteFurniture, { loading: deletingFurniture }] = useInventoryMasterDataDeleteChanges();
    const [, { loading: reorderingFurniture }, reorderFurniture] = useInventoryMasterDataUpdateChanges();
    const showSnackbar = useSnackbar();

    const handleEdit = useCallback(
        (data: InventoryMasterDataRow) => {
            setSelectedRow(data);
            openDialog();
        },
        [openDialog]
    );

    const showDeleteDialog = useCallback(
        (data: InventoryMasterDataRow) => {
            setFurnitureToDelete(data);
            openDeleteDialog();
        },
        [openDeleteDialog]
    );

    const columns = useMemo(() => {
        return [
            ...defaultColumns,
            {
                headerName: 'Actions',
                field: 'actions',
                cellRenderer: (params: ICellRendererParams) => {
                    return (
                        <>
                            {deletingFurniture && furnitureToDelete?.id === params.data.id ? (
                                <Stack
                                    direction="row"
                                    sx={{
                                        alignItems: 'center',
                                        gap: 2,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        Deleting...
                                    </Typography>
                                    <CircularProgress size={20} />
                                </Stack>
                            ) : (
                                <Stack
                                    direction="row"
                                    sx={{
                                        justifyContent: 'space-around',
                                        alignItems: 'center',
                                    }}
                                >
                                    <IconButton color="info" onClick={() => handleEdit(params.data)}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        disabled={deletingFurniture}
                                        color="error"
                                        onClick={() => showDeleteDialog(params.data)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Stack>
                            )}
                        </>
                    );
                },
                cellStyle: { textAlign: 'center' },
                sortable: false,
                filter: false,
            },
        ];
    }, [deletingFurniture, furnitureToDelete, handleEdit, showDeleteDialog]);

    const handleDelete = useCallback(() => {
        if (furnitureToDelete) {
            const paylod = { id: furnitureToDelete.id };
            void deleteFurniture(paylod);
            closeDeleteDialog();
        }
    }, [furnitureToDelete, deleteFurniture, closeDeleteDialog]);

    const handleRowDragEnd = useCallback(
        (event: RowDragEndEvent) => {
            if (!rows || reorderingFurniture) return;

            const reorderedRows: InventoryMasterDataRow[] = [];
            event.api.forEachNodeAfterFilterAndSort((node) => {
                if (node.data) reorderedRows.push(node.data as InventoryMasterDataRow);
            });

            if (reorderedRows.length !== rows.length) {
                showSnackbar('Clear filters before reordering furniture.', 'warning');
                return;
            }

            if (reorderedRows.every((row, index) => row.id === rows[index]?.id)) return;

            void reorderFurniture(buildInventoryMasterDataReorderInputs(reorderedRows));
        },
        [reorderFurniture, reorderingFurniture, rows, showSnackbar]
    );

    const hideDeleteDialog = useCallback(() => {
        setFurnitureToDelete(null);
        closeDeleteDialog();
    }, [closeDeleteDialog]);

    const handleClose = useCallback(() => {
        setSelectedRow(null);
        closeDialog();
    }, [closeDialog]);

    useKeyboardShortcut({
        shortcutName: 'NEW',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            openDialog();
        },
    });

    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    useCustomAgGridNavigation({
        api: gridApi,
        tableWrapperRef,
    });

    const onGridReady = useCallback(({ api }: GridReadyEvent) => {
        setGridApi(api);
    }, []);

    const nextSortOrder = (rows ?? []).reduce((max, row) => Math.max(max, row.sortOrder), -1) + 1;

    useKeyboardShortcut({
        shortcutName: 'EDIT/VIEW',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            const selectedRows = gridApi?.getSelectedRows();
            if (selectedRows?.length === 1) {
                handleEdit(selectedRows[0]);
            } else {
                showSnackbar('Please select a single furniture item to edit.', 'warning');
            }
        },
    });

    useKeyboardShortcut({
        shortcutName: 'DELETE',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            const selectedRows = gridApi?.getSelectedRows();
            if (selectedRows?.length === 1) {
                showDeleteDialog(selectedRows[0]);
            } else {
                showSnackbar('Please select a single furniture item to delete.', 'warning');
            }
        },
    });

    return (
        <>
            <Stack spacing={2}>
                <SmartModuleTitleCard />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}
                >
                    <Button
                        size="medium"
                        variant="contained"
                        color="info"
                        startIcon={<Add />}
                        disabled={loading || reorderingFurniture}
                        onClick={() => openDialog()}
                    >
                        Add new furniture
                    </Button>
                </Box>
                <Container>
                    <TableWrapper sx={{ flex: 1 }} className="ag-theme-alpine-custom-compact" ref={tableWrapperRef}>
                        <DataTable
                            isNumberTable={false}
                            loading={loading || reorderingFurniture}
                            columns={columns}
                            defaultColDef={defaultColDef}
                            rows={rows}
                            onGridReady={onGridReady}
                            rowDragManaged
                            animateRows
                            onRowDragEnd={handleRowDragEnd}
                            rowSelection={{
                                mode: 'singleRow',
                                checkboxes: false,
                                enableClickSelection: true,
                            }}
                            suppressCellFocus
                        ></DataTable>
                    </TableWrapper>
                </Container>
            </Stack>
            {isOpen && (
                <AddFurnitureDialog
                    isOpen={isOpen}
                    close={() => handleClose()}
                    furnitureRow={selectedRow}
                    nextSortOrder={nextSortOrder}
                />
            )}
            {isDeleteOpen && (
                <DeleteDialog
                    open={!!isDeleteOpen}
                    onCancel={hideDeleteDialog}
                    onDelete={handleDelete}
                    title="Delete furniture?"
                    content={`Are you sure you want to delete furniture '${furnitureToDelete?.name}'? Deleting this furniture is permanent and cannot be undone.`}
                />
            )}
        </>
    );
};
